"use client"

import axios from "axios"
import moment from "moment"
import React, { useEffect, useRef, useState } from "react"
import { NotificationManager } from "react-notifications"
import { useSelector } from "react-redux"
import { Link, useNavigate } from "react-router-dom"
import Select from "react-select"
import contact from "../../assets/svgs/account.svg"
import DownArrow from "../../assets/svgs/down-arrow.svg"
import sortAsce from "../../assets/svgs/sort-ascending.svg"
import sortDesc from "../../assets/svgs/sort-descending.svg"
import swap from "../../assets/svgs/swap-vertical.svg"
import UpArrow from "../../assets/svgs/up-arrow.svg"
import ActionsMenu from "../../components/ActionsMenu"
import BarData from "../../components/Charts/BarData"
import CircleChart from "../../components/Charts/CircleChart"
import ColumnCustomizer from "../../components/ColumnCustomizer"
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal"
import Loader from "../../components/Loader"
import ContactPagination from "../../components/Pagination/ContactPagination"
import useAuth from "../../hooks/useAuth"
import BaseLayout from "../../layouts/BaseLayout"
import { BASE_URL } from "../../utils/Element"
import { initialProposalFilterData } from "../../utils/initialData"
import PropertyPortfolioModal from "../ContractToClosed/components/PropertyPortfolioModal"
import AddLeadModal from "../Leads/AddLeadModal"
import LeadDataContainer from "../Leads/components/LeadDataContainer"
import LeadDataContainer2 from "../Leads/components/LeadDataContainer2"
import LeadsFilter from "../Leads/components/LeadsFilter"
import EditLeadModal from "../Leads/EditLeadModal"
import ChangeProposalStatusModal from "./modals/ChangeProposalStatusModal"

const Proposals = () => {
  const [config] = useAuth()
  const navigate = useNavigate()

  // ─── Search + Debounce states ────────────────────────────────
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const debounceTimer = useRef(null)
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState("")
  const [showColumnModal, setShowColumnModal] = useState(false)
  const [selectedPortfolioId, setSelectedPortfolioId] = useState("") // ← New State
  const [currentPage, setCurrentPage] = useState(1)
  const [itemPerPage, setItemPerPage] = useState(50)
  const [proposalData, setProposalData] = useState([])
  const [selectedStatus, setSelectedStatus] = useState(0)
  const [bovDashboard, setBovDashboard] = useState()
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [disableDelete, setDisableDelete] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [expandedRows, setExpandedRows] = useState({})
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [sortBy, setSortBy] = useState("id")
  const [rowStatuses, setRowStatuses] = useState({})
  const [activeTab, setActiveTab] = useState(3)
  const [sortDirection, setSortDirection] = useState("asc")
  const [bdsOptions, setBdsOptions] = useState([])
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed)
  const [paginationData, setPaginationData] = useState({
    totalItems: 0,
    from: 0,
    to: 0,
    totalPages: 0,
  })
  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData")
    return localFilterData ? JSON.parse(localFilterData) : initialProposalFilterData
  })

  // ==================== COLUMN CUSTOMIZATION ====================
  const allColumns = [
    { key: 'details', label: 'Details', fixed: true },
    { key: 'bov_date', label: 'BOV Date', fixed: true },
    { key: 'contact_name', label: 'Contact Name' },
    { key: 'lead_type', label: 'Lead Type' },
    { key: 'status', label: 'Status' },
    { key: 'property_acquisition', label: 'Property / Acquisition Criteria' },
    { key: 'suggested_sale_price', label: 'Suggested Sale Price' },
    { key: 'suggested_cap_rate', label: 'Suggested Cap Rate' },
    { key: 'actions', label: 'Actions', fixed: true },
  ];

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('proposalsVisibleColumns');
    return saved
      ? JSON.parse(saved)
      : allColumns.map(col => col.key); // Show all by default
  });
  // ============================================================

  const [selectedUser, setSelectedUser] = useState(null)
  const [originalUserData, setOriginalUserData] = useState(null)
  const [isSwitchingUser, setIsSwitchingUser] = useState(false)
  const [filterStatus, setFilterStatus] = useState({
    new_bov_quarter: false,
    bov_count_ytd: false,
    lead_to_bov_conversion: false,
    warm_hot_bov_notcontacted: false,
    less_than_1m: false,
    between_1m_3m: false,
    greater_than_3m: false,
  })

  // ─── Debounce logic ───────────────────────────────────────────
  const handleSearchChange = (e) => {
    const value = e.target.value
    const trimmed = value.startsWith(" ") ? value.trimStart() : value
    setSearch(trimmed)
  }

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = setTimeout(() => {
      // Only update if empty or ≥ 3 characters
      if (search.length === 0 || search.length >= 3) {
        setDebouncedSearch(search.trim())
      }
    }, 450)

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [search])

  const handleNext = () => {
    if (currentPage < paginationData.totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const buildQueryParams = (filters) => {
    let params = `page=${currentPage}&per_page=${itemPerPage}&type=proposal`

    // Use debounced search value
    if (debouncedSearch) {
      params += `&search=${encodeURIComponent(debouncedSearch)}`
    }

    const {
      bds,
      broker,
      status,
      created_at,
      created_end_date,
      created_start_date,
      contact,
      lead_type,
      link_id,
      property,
      buyer_id,
    } = filters

    if (bds) params += `&bds=${encodeURIComponent(bds)}`
    if (broker) params += `&broker=${encodeURIComponent(broker)}`
    if (status) {
      params += `&status=${encodeURIComponent(status === "followup" ? 0 : status === "won" ? 1 : 2)}`
    }
    if (created_at) params += `&created_at=${encodeURIComponent(created_at)}`
    if (created_start_date) params += `&created_start_date=${encodeURIComponent(created_start_date)}`
    if (created_end_date) params += `&created_end_date=${encodeURIComponent(created_end_date)}`
    if (contact?.id) params += `&contact=${encodeURIComponent(contact.id)}`
    if (lead_type) params += `&lead_type=${encodeURIComponent(lead_type === "acquisition" ? 1 : 0)}`
    if (link_id) params += `&link_id=${encodeURIComponent(link_id)}`
    if (property?.id) params += `&property_id=${encodeURIComponent(property.id)}`
    if (buyer_id) params += `&buyer_id=${encodeURIComponent(buyer_id)}`

    // Filter status flags
    if (filterStatus.new_bov_quarter) params += `&new_bov_quarter=true`
    if (filterStatus.bov_count_ytd) params += `&bov_count_ytd=true`
    if (filterStatus.lead_to_bov_conversion) params += `&lead_to_bov_conversion=true`
    if (filterStatus.warm_hot_bov_notcontacted) params += `&warm_hot_bov_notcontacted=true`
    if (filterStatus.less_than_1m) params += `&less_than_1m=true`
    if (filterStatus.between_1m_3m) params += `&between_1m_3m=true`
    if (filterStatus.greater_than_3m) params += `&greater_than_3m=true`

    if (sortDirection && sortBy !== "id") params += `&sort_direction=${sortDirection}`
    if (sortBy !== "id") params += `&sort_by=${sortBy}`

    return params
  }

  const fetchProposals = (filters = filterData) => {
    const queryParams = buildQueryParams(filters)
    setLoading(true)

    axios
      .get(`${BASE_URL}/list-leads?active_tab=${activeTab}&${queryParams}`, config)
      .then((res) => {
        const value = res?.data?.leadsList
        setProposalData(value?.data || [])
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        })
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message)
        }
      })
      .finally(() => setLoading(false))
  }

  // ─── Main triggers ────────────────────────────────────────────
  useEffect(() => {
    fetchProposals(filterData)
  }, [
    sortDirection,
    sortBy,
    filterStatus,
    activeTab,
    currentPage,
    itemPerPage,
    debouncedSearch,   // ← key addition
    selectedUser,
  ])

  // Reset page when search or major filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, activeTab, filterStatus, selectedUser])

  const onSuccess = (filters) => {
    fetchProposals(filters || filterData)
  }

  const handleDelete = () => {
    setDisableDelete(true)
    axios
      .delete(`${BASE_URL}/delete-lead/${selectedId}`, config)
      .then((res) => {
        setSelectedId("")
        onSuccess()
        setShowDeleteModal(false)
        if (res?.data?.message) NotificationManager.success(res.data.message)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
      .finally(() => setDisableDelete(false))
  }

  const statusOptions = [
    { value: 2, label: "Cold" },
    { value: 3, label: "Warm" },
    { value: 1, label: "Hot" },
    { value: 4, label: "Won" },
    { value: 5, label: "Lost" },
  ]

  const colorMap = { 1: "#ff0000", 3: "#ffb952", 2: "blue", 5: "gray", 4: "green" }

  const handleStatusChange = (value, bovId) => {
    setRowStatuses((prev) => ({ ...prev, [bovId]: value }))

    axios
      .post(
        `${BASE_URL}/bov-status-change/${bovId}`,
        { id: bovId, status: value },
        config
      )
      .then((res) => {
        if (res?.data?.message) NotificationManager.success(res.data.message)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
  }

  const data = bovDashboard?.bov_status || []

  const BarGraphData = [
    { name: "<$1M", count: bovDashboard?.count_less_than_million || 0, key: "less_than_1m" },
    { name: "$1M-$3M", count: bovDashboard?.count_between_than_three_million || 0, key: "between_1m_3m" },
    { name: ">$3M", count: bovDashboard?.count_greater_than_three_million || 0, key: "greater_than_3m" },
  ]

  const fetchDashboardData = () => {
    let url = `${BASE_URL}/dashboard-bov?active_tab=${activeTab}`
    if (selectedUser?.value) url += `&show_user_id=${selectedUser.value}`

    axios
      .get(url, config)
      .then((res) => setBovDashboard(res?.data?.bov_dashboard))
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
  }

  useEffect(() => {
    if ([0, 2].includes(activeTab)) fetchDashboardData()
  }, [selectedUser, activeTab])

  const fetchUsers = () => {
    axios
      .get(`${BASE_URL}/get-datalisting?list=users`, config)
      .then((res) => {
        const options = res?.data?.data?.map((el) => ({
          value: el.id,
          label: `${el.first_name} ${el.last_name}`,
          role: el.role_id,
        }))
        setBdsOptions(options)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  // ==================== Dropdown View Options ====================
  const viewOptions = [
    { value: 0, label: "My Dashboard" },
    { value: 1, label: "My BOV" },
    { value: 2, label: "Team Dashboard" },
    { value: 3, label: "Team BOV" },
  ];

  const handleViewChange = (e) => {
    const newTab = parseInt(e.target.value);
    setActiveTab(newTab);
    setCurrentPage(1);
    setExpandedRows({});
    // Reset filterStatus when switching tabs
    setFilterStatus({
      new_bov_quarter: false,
      bov_count_ytd: false,
      lead_to_bov_conversion: false,
      warm_hot_bov_notcontacted: false,
      less_than_1m: false,
      between_1m_3m: false,
      greater_than_3m: false,
    });
  };

  const handleBoxClick = (key) => {
    let targetTab = activeTab === 0 ? 1 : 3

    setActiveTab(targetTab)
    setCurrentPage(1)

    setFilterStatus({
      new_bov_quarter: key === "new_bov_quarter",
      bov_count_ytd: key === "bov_count_ytd",
      lead_to_bov_conversion: key === "lead_to_bov_conversion",
      warm_hot_bov_notcontacted: key === "warm_hot_bov_notcontacted",
      less_than_1m: key === "less_than_1m",
      between_1m_3m: key === "between_1m_3m",
      greater_than_3m: key === "greater_than_3m",
    })
  }

  const handleUserSwitch = (option) => {
    if (!option) {
      setSelectedUser(null)
      setIsSwitchingUser(false)
      onSuccess()
      return
    }

    if (!isSwitchingUser) {
      setOriginalUserData(proposalData)
      setIsSwitchingUser(true)
    }

    setSelectedUser(option)
    onSuccess()
  }

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const getDisplayText = (el) => {
    if (el?.lead_type === 0 && el.portfolio !== 1) {
      if (!el?.link?.property_name || !el?.link_address_name) {
        return ""
      }
      return (
        <>
          <span>{el.link.property_name}</span>
          <br />
          <span>{el.link_address_name.street}</span>
          <br />
          <span>{el.link_address_name.city}, {el.link_address_name.state}</span>
        </>
      )
    }
    if (el?.lead_type === 1 && el.portfolio !== 1) {
      return el?.link?.property_type?.[0]?.type || ""
    }
    return el?.link?.name || ""
  }

  return (
    <BaseLayout>
      <div className="contact-header mb-3">
        <p className="head-1 dark-H">BOV</p>
        <div className="md:flex block gap-4 items-center">
          <div className="flex gap-4 justify-between items-center w-full md:w-auto flex-wrap">
            {/* Dropdown - Team BOV is Default */}
            <div className="relative min-w-[240px]">
              <select
                value={activeTab}
                onChange={handleViewChange}
                className="w-full bg-white rounded-lg px-4 py-2.5 body-S dark-M cursor-pointer appearance-none"
              >
                {viewOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                <img src={DownArrow} alt="down" className="w-4 h-4" />
              </div>
            </div>

            <LeadsFilter
              filterData={filterData}
              onSetFilterData={setFilterData}
              from="BOV"
              onCallApiAgain={onSuccess}
            />

            <div className="search-box contacts relative flex-1 min-w-[200px]">
              <input
                type="text"
                className="body-S w-full"
                placeholder="Search contact name"
                value={search}
                onChange={handleSearchChange}
                style={{ border: "1px solid #D8D8D8" }}
              />
            </div>

            {/* New Customize Columns Button */}
            {(activeTab === 1 || activeTab === 3) && (
              <button
                className="add-contact-button green-bg-H light-L body-S flex items-center gap-1"
                onClick={() => setShowColumnModal(true)}
              >
                Columns ⚙️
              </button>
            )}

            {/* Uncomment if you want user select dropdown */}
            {/* <div className="relative ml-2 z-50">
              <Select
                className="body-S min-w-[200px]"
                placeholder="Select user"
                value={selectedUser}
                options={bdsOptions}
                onChange={handleUserSwitch}
                isClearable={true}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div> */}
          </div>
        </div>
      </div>

      {(activeTab === 1 || activeTab === 3) && (
        <div className="light-bg-L pb-3 table-container">
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
            {loading ? (
              <Loader />
            ) : proposalData?.length === 0 ? (
              <p className="body-N text-center mt-6 p-10">No BOV Available</p>
            ) : (
              <table className="team-leads-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    {visibleColumns.includes('details') && (
                      <th><p>Details</p></th>
                    )}
                    {visibleColumns.includes('bov_date') && (
                      <th>
                        <div className="table-header">
                          BOV Date
                          <img
                            role="button"
                            src={sortBy !== "proposal_date" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            alt="sort icon"
                            onClick={() => {
                              setSortBy("proposal_date")
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                            }}
                          />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('contact_name') && (
                      <th>
                        <div className="table-header">
                          Contact Name
                          <img
                            role="button"
                            src={sortBy !== "contact_id" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            alt="sort icon"
                            onClick={() => {
                              setSortBy("contact_id")
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                            }}
                          />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('lead_type') && (
                      <th>
                        <div className="table-header">
                          Lead Type
                          <img
                            role="button"
                            src={sortBy !== "type" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            alt="sort icon"
                            onClick={() => {
                              setSortBy("type")
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                            }}
                          />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('status') && (
                      <th>
                        <div className="table-header">
                          Status
                          <img
                            role="button"
                            src={sortBy !== "status" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            alt="sort icon"
                            onClick={() => {
                              setSortBy("status")
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                            }}
                          />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('property_acquisition') && (
                      <th className="green-H">
                        <div className="table-header">
                          Property /
                          <br />
                          Acquisition Criteria
                          <img
                            role="button"
                            src={sortBy !== "property_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("property_name")
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                            }}
                            alt="sort icon"
                          />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('suggested_sale_price') && (
                      <th>
                        <div className="table-header">
                          Suggested <br />Sale Price
                          <img
                            role="button"
                            src={sortBy !== "suggested_sale_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            alt="sort icon"
                            onClick={() => {
                              setSortBy("suggested_sale_price")
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                            }}
                          />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('suggested_cap_rate') && (
                      <th>
                        <div className="table-header">
                          Suggested <br />Cap Rate
                          <img
                            role="button"
                            src={sortBy !== "suggested_cap_rate" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            alt="sort icon"
                            onClick={() => {
                              setSortBy("suggested_cap_rate")
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                            }}
                          />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('actions') && (
                      <th>
                        <div className="table-header">Actions</div>
                      </th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {proposalData.map((el, idx) => (
                    <React.Fragment key={idx}>
                      <tr key={idx} className="dark-H body-N">
                        {visibleColumns.includes('details') && (
                          <td>
                            <img
                              role="button"
                              src={expandedRows[el.id] ? UpArrow : DownArrow}
                              alt="expand"
                              className="sidebar-icons"
                              onClick={() => toggleRowDetails(el.id)}
                            />
                          </td>
                        )}

                        {visibleColumns.includes('bov_date') && (
                          <td className="dark-M">
                            {moment(el?.proposal_date).format("MM/DD/YYYY")}
                          </td>
                        )}

                        {visibleColumns.includes('contact_name') && (
                          <td className="flex items-center head-5 green-H gap-2 capitalize">
                            <Link
                              to={`/contact/${el?.contact?.id}`}
                              className="flex items-center gap-2"
                              onClick={(e) => {
                                e.preventDefault()
                                navigate(`/contact/${el?.contact?.id}`)
                              }}
                            >
                              {el?.contact && (
                                <img src={contact} alt="contact" className="sidebar-icons" />
                              )}
                              {el?.contact?.first_name} {el?.contact?.last_name}
                            </Link>
                          </td>
                        )}

                        {visibleColumns.includes('lead_type') && (
                          <td>{el?.lead_type === 0 ? "Disposition" : "Acquisition"}</td>
                        )}

                        {visibleColumns.includes('status') && (
                          <td>
                            <Select
                              options={statusOptions}
                              value={
                                statusOptions.find(
                                  (option) => option.value === (rowStatuses[el.id] || el.status)
                                ) || null
                              }
                              onChange={(option) => handleStatusChange(option.value, el.id)}
                              placeholder="Status"
                              className="body-N lead-width"
                              styles={{
                                singleValue: (base, { data }) => ({ ...base, color: colorMap[data.value] }),
                                option: (base, { isFocused }) => ({
                                  ...base,
                                  backgroundColor: isFocused ? "#f0f0f0" : "white",
                                  color: "black",
                                }),
                              }}
                            />
                          </td>
                        )}

                        {visibleColumns.includes('property_acquisition') && (
                          <td className="head-5 capitalize flex items-start gap-3">
                            {el?.portfolio === 1 && (
                              <p
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedPortfolioId(el?.link?.id);
                                  setShowPropertyModal(true);
                                }}
                                className="cursor-pointer"
                              >
                                +
                              </p>
                            )}

                            <div className="flex-1">
                              <Link
                                to={
                                  el?.portfolio === 0
                                    ? `/property/${el?.property_id}`
                                    : el?.link?.owner_info_type === "contact"
                                      ? `/contact/${el?.link?.owner?.id}`
                                      : `/company/${el?.link?.owner?.id}`
                                }
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (el?.portfolio === 0) {
                                    navigate(`/property/${el?.property_id}`);
                                  } else if (el?.link?.owner_info_type === "contact") {
                                    navigate(`/contact/${el?.link?.owner?.id}`);
                                  } else if (el?.link?.owner_info_type === "company") {
                                    navigate(`/company/${el?.link?.owner?.id}`);
                                  }
                                }}
                              >
                                {el?.link?.property_name} <br />
                                {el?.link?.street_address} <br />
                                {el?.link?.state && el?.link?.city ? ` ${el?.link?.city} ,  ${el?.link?.state} ` : ""}
                              </Link>
                            </div>
                          </td>
                        )}

                        {visibleColumns.includes('suggested_sale_price') && (
                          <td className="dark-M text-center">${el?.suggested_sale_price_format || "—"}</td>
                        )}

                        {visibleColumns.includes('suggested_cap_rate') && (
                          <td className="dark-M text-center">
                            {el?.suggested_cap_rate_format
                              ? `${el.suggested_cap_rate_format}%`
                              : "—"}
                          </td>
                        )}

                        {visibleColumns.includes('actions') && (
                          <td className="flex gap-3 items-center w-full justify-center">
                            <ActionsMenu
                              from="BOV"
                              handleEdit={() => {
                                setShowEditModal(true)
                                setSelectedId(el?.id)
                              }}
                              handleDelete={() => {
                                setSelectedId(el?.id)
                                setShowDeleteModal(true)
                              }}
                              otherOptionTitle={"Convert to Exclusives"}
                              handleExclusiveOption={() => {
                                setShowStatusModal(true)
                                setSelectedId(el?.id)
                              }}
                            />
                          </td>
                        )}
                      </tr>

                      {expandedRows[el.id] && (
                        <tr>
                          <td colSpan="11">
                            <div className="expanded-row-content p-4 bg-[rgb(244,244,244)] rounded-md">
                              <div className="flex items-center gap-6 mb-7">
                                <div>
                                  <label className="dark-H head-4 block">BDS</label>
                                  <p className="body-N dark-H pt-2 min-h-[1.5rem]">
                                    <span>
                                      <Link
                                        to={`/user/${el?.bds?.id}`}
                                        onClick={(e) => {
                                          e.preventDefault()
                                          navigate(`/user/${el?.bds?.id}`)
                                        }}
                                      >
                                        {el?.bds?.first_name} {el?.bds?.last_name}
                                      </Link>
                                    </span>
                                  </p>
                                </div>

                                <div>
                                  <label className="dark-H head-4 block">Broker</label>
                                  <p className="body-N dark-H pt-2 min-h-[1.5rem] space-y-1">
                                    {el?.broker_details?.length > 0 ? (
                                      el.broker_details.map((broker) => (
                                        <span key={broker.id} className="block">
                                          <Link
                                            to={`/user/${broker.id}`}
                                            onClick={(e) => {
                                              e.preventDefault()
                                              navigate(`/user/${broker.id}`)
                                            }}
                                            className="text-gray-800 hover:text-green-700"
                                          >
                                            {broker.first_name} {broker.last_name}
                                          </Link>
                                        </span>
                                      ))
                                    ) : (
                                      <span>—</span>
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {proposalData?.length > 0 && (
            <ContactPagination
              activeTab={activeTab}
              paginationData={paginationData}
              handleNext={handleNext}
              handlePrev={handlePrev}
              onSuccess={onSuccess}
              itemPerPage={itemPerPage}
              handleItemPerPageSelect={(value) => {
                setItemPerPage(value)
                setCurrentPage(1)
              }}
            />
          )}
        </div>
      )}

      {(activeTab === 0 || activeTab === 2) && (
        <>
          <div className="flex gap-3">
            <div className="w-[65%]">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                <LeadDataContainer
                  text="NEW BOVs THIS QUARTER"
                  number={bovDashboard?.bov_quarter_count || 0}
                  className="min-h-[8rem] w-full"
                  onClick={() => handleBoxClick("new_bov_quarter")}
                />

                <LeadDataContainer
                  text="TOTAL BOVs YTD"
                  number={bovDashboard?.bov_count_ytd || 0}
                  className="min-h-[8rem] w-full"
                  onClick={() => handleBoxClick("bov_count_ytd")}
                />

                <LeadDataContainer
                  text="YTD LEAD TO BOV CONVERSION RATE"
                  number={`${bovDashboard?.conversion_rate || 0}%`}
                  className="min-h-[8rem] w-full"
                  onClick={() => handleBoxClick("lead_to_bov_conversion")}
                />

                <LeadDataContainer
                  text="WARM & HOT BOVs NOT CONTACTED IN 14+ DAYS"
                  number={bovDashboard?.bov_status_count || 0}
                  className="min-h-[8rem] w-full"
                  onClick={() => handleBoxClick("warm_hot_bov_notcontacted")}
                />
              </div>

              <div className="flex items-center gap-5">
                <CircleChart
                  Title="BOV Stages"
                  TotalTitle="Total BOV"
                  Total={bovDashboard?.bov_count || 0}
                  data={data}
                />
                <BarData
                  className="w-[50%]"
                  Title="Price Point of BOVs"
                  BarGraphData={BarGraphData}
                  onBarClick={(key) => handleBoxClick(key)}
                />
              </div>
            </div>

            <div className="w-[35%]">
              <LeadDataContainer2
                text="HOT BOV"
                data={bovDashboard?.bov_status_hot ?? []}
                columns={[
                  { key: "contact_name", label: "Contact Name" },
                  {
                    key: "link_name",
                    label: (
                      <>
                        Property/
                        <br />
                        Acquisition Criteria
                      </>
                    ),
                  },
                ]}
              />

              <LeadDataContainer2
                text="WARM BOV"
                data={bovDashboard?.bov_status_warm ?? []}
                columns={[
                  { key: "contact_name", label: "Contact Name" },
                  {
                    key: "link_name",
                    label: (
                      <>
                        Property/
                        <br />
                        Acquisition Criteria
                      </>
                    ),
                  },
                ]}
              />
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      <AddLeadModal from="BOV" showModal={showAddModal} onClose={() => setShowAddModal(false)} onCallApi={onSuccess} />

      <EditLeadModal
        showModal={showEditModal}
        onClose={() => setShowEditModal(false)}
        id={selectedId}
        onCallApi={() => {
          setSelectedId("")
          onSuccess()
        }}
        from="BOV"
      />

      <ChangeProposalStatusModal
        showModal={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        id={selectedId}
        status={selectedStatus}
        onCallApi={() => {
          setSelectedId("")
          onSuccess()
        }}
      />

      <DeleteConfirmationModal
        showModal={showDeleteModal}
        disable={disableDelete}
        onClose={() => setShowDeleteModal(false)}
        handleDelete={handleDelete}
      />


      <PropertyPortfolioModal
        isOpen={showPropertyModal}
        onClose={() => {
          setShowPropertyModal(false);
          setSelectedProperty(null);
        }}
        id={selectedPortfolioId}
        property={selectedProperty}
      />

      {/* Column Customizer Modal */}
      {showColumnModal && (
        <ColumnCustomizer
          allColumns={allColumns}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
          storageKey="proposalsVisibleColumns"
          onClose={() => setShowColumnModal(false)}
        />
      )}
    </BaseLayout>
  )
}

export default Proposals